import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { defaultLocale, Locale } from "./locale";

export { Locale } from "./locale";

export type LocalizedStrings<Localization> = {
  [Locale.EN]: Localization;
  [Locale.ES]: Localization;
};

export const LocalizationContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
}>({
  locale: defaultLocale,
  setLocale: () => {},
});

type LocalizationProviderProps = PropsWithChildren<{
  initialLocale: Locale;
}>;

export const LocalizationProvider = ({
  children,
  initialLocale,
}: LocalizationProviderProps) => {
  const [locale, setLocale] = useState(initialLocale);

  useEffect(() => {
    setLocale(initialLocale);
  }, [initialLocale]);

  return (
    <LocalizationContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export function useLocalization<Localization>(
  strings: LocalizedStrings<Localization>
) {
  const { locale } = useContext(LocalizationContext);

  return useCallback(
    <K extends keyof Localization>(key: K): Localization[K] =>
      strings[locale][key],
    [strings, locale]
  );
}
